/**
 * Compliance Document Generator (CDG) — DTOs, enums, query keys, and skip-and-warn
 * types.
 *
 * These mirror the locked CDG API contract (DL-60048) verbatim:
 *  - JSON fields are **camelCase**.
 *  - DTO type names use the `<Entity>Dto` suffix to match the backend contract 1:1
 *    (this is the cross-stack seam the backend implements against — DL-59571).
 *  - The response envelope and pagination shapes are NOT redefined here. Consumers
 *    wrap these payloads with `Response<T>` / `PaginateResponse<T>` from `types`.
 *
 * Consumed by the ReactQuery hooks (DL-59572) and the wizard/table track.
 */

// ---------------------------------------------------------------------------
// Enums / status unions
// ---------------------------------------------------------------------------

/**
 * Lifecycle/display status of a generated compliance document.
 *
 * ❓CONFIRM with the backend: exact wire values, and whether `ExpiringSoon` /
 * `Expired` are stored or derived from the document/cert expiry. `Signed` is not
 * produced in the MVP (upload cut) but is retained for completeness so fixtures
 * can cover every chip token. Tracked in DL-59571.
 */
export type DocumentStatus = 'Generated' | 'Signed' | 'ExpiringSoon' | 'Expired' | 'Archived';

/** Source discriminator for an available certificate. */
export type AvailableCertSource = 'Style' | 'Item';

/** Why a cert option is non-selectable in the picker. */
export type AvailableCertDisabledReason = 'Expired' | 'Pending approval' | 'Inaccessible';

// ---------------------------------------------------------------------------
// Available certificates — GET /api/ComplianceDocGenerator/AvailableCerts
// ---------------------------------------------------------------------------

/** A single mergeable certificate file within a cert (cert → file is 1:N). */
export type AvailableCertFileDto = {
  /** GUID — `ComplianceCertificateFile.Id`; echoed into `download.certificateFileIds[]`. */
  certificateFileId: string;
  fileName: string;
  /** Merge order WITHIN the cert (`ComplianceCertificateFile.Position`). */
  position: number;
};

/**
 * A certificate available for inclusion as an appendix, deduped on `complianceId`
 * and pre-sorted in canonical merge order (sort lives in the sproc).
 */
export type AvailableCertDto = {
  /** BIGINT — the cert (dedup key); = `ComplianceCertificateFile.EntityId`. */
  complianceId: number;
  source: AvailableCertSource;
  sourceId: number;
  sourceName: string;
  /** Item = STRING_AGG of categories; Style = null. */
  sourceCategory: string | null;
  isAccessible: boolean;
  isDisabled: boolean;
  disabledReason: AvailableCertDisabledReason | null;
  /** 1:N — a cert has 1..N files (prod: 79% have 1, 21% have ≥2, max ~109). */
  files: AvailableCertFileDto[];

  // ❓CONFIRM — provisional, OPTIONAL until the backend enumerates the exact
  // AvailableCert fields. The cert picker (DL-59578) wants these for display.
  certNumber?: string | null;
  expiryDate?: string | null;
  certificateTypeName?: string | null;
  certState?: number | null;
};

// ---------------------------------------------------------------------------
// Compliance documents — /api/ComplianceDocument
// ---------------------------------------------------------------------------

/** A cert-file association on a generated document (`ComplianceDocumentCertFile` join row). */
export type ComplianceDocumentCertFileDto = {
  /** GUID — `ComplianceCertificateFile.Id`. */
  complianceCertificateFileId: string;
  position: number;
  fileHash: string | null;
};

/** Full document — GET /api/ComplianceDocument/{id}. */
export type ComplianceDocumentDto = {
  /** GUID. */
  id: string;
  orgId: number;
  templateId: number;
  templateEditCount: number;
  styleId: number;
  complianceId: number;
  fileName: string;
  status: DocumentStatus;
  dataSnapshotHash: string;
  blobPath: string;
  notes: string | null;
  certFiles: ComplianceDocumentCertFileDto[];
  /** User id of the creator (audit `CreatedBy`). */
  createdBy: number;
  createdAt: string;
  lastUpdated: string;
};

/**
 * List-row projection — GET /api/ComplianceDocument.
 *
 * ❗CONTRACT GAP: the contract does NOT yet enumerate this DTO's fields. The shape
 * below is PROPOSED for the documents table (DL-59574) and is tracked in DL-59571
 * for backend confirmation. The display `*Name` fields are provisional (the list
 * join may or may not include them).
 */
export type ComplianceDocumentListItemDto = {
  id: string;
  fileName: string;
  status: DocumentStatus;
  templateId: number;
  templateName?: string;
  /** Template category shown in the list `Type` column (e.g. `Regulatory`); also the `templateType` filter value. */
  templateType: string;
  styleId: number;
  styleName?: string;
  styleUserDefinedId?: string;
  complianceId: number;
  /** Number of certificate files merged into the document — the list `Certs` column. */
  certCount: number;
  createdBy: number;
  createdByName?: string;
  createdAt: string;
  lastUpdated: string;
};

// ---------------------------------------------------------------------------
// Reused lookups (document only)
// ---------------------------------------------------------------------------

export type ComplianceDocTemplateDto = {
  id: number;
  name: string;
  version: number;
  /** Template category (e.g. `Regulatory`); surfaced as a document's `templateType` and the list `Type` filter options. */
  type: string;
};

export type ComplianceDocTemplateFieldDto = {
  id: number;
  fieldId: number;
  label: string;
  resultSet: string | null;
};

/** GET /StylesForComplianceDocGenerator/{complianceId}. */
export type ComplianceGeneratorStyleDto = {
  id: number;
  name: string;
  userDefinedId: string;
};

// ---------------------------------------------------------------------------
// Generator preview / download commands
// ---------------------------------------------------------------------------

export type ComplianceDocGeneratorCommand = {
  templateId: number;
  styleId: number;
  complianceId: number;
  timeZone: string;
};

/** Download extends the generator command with the chosen cert-file GUIDs. */
export type ComplianceDocGeneratorDownloadCommand = ComplianceDocGeneratorCommand & {
  /** GUID[] — chosen `ComplianceCertificateFile` ids; defaults to `[]` (legacy blob caller). */
  certificateFileIds: string[];
};

/**
 * Preview node (structural mirror of the rendered document).
 *
 * ❗CONTRACT GAP: the contract does NOT yet enumerate the preview JSON. The
 * discriminated union below is PROPOSED and is tied to the unresolved
 * preview-scope decision (DL-60056). Consumers MUST handle unknown `kind`.
 */
export type PreviewNodeKind = 'section' | 'fieldRow' | 'naRow' | 'paragraph';

export type PreviewNode = {
  kind: PreviewNodeKind;
  label?: string;
  /** Rendered value; a `[MISSING:<field>]` token when a required field is absent. */
  value?: string | null;
  /** Convenience flag derived from a `[MISSING:]` value; FE derives Step-3 readiness. */
  isMissing?: boolean;
  children?: PreviewNode[];
};

export type ComplianceDocGeneratorDetails = {
  sections: PreviewNode[];
};

// ---------------------------------------------------------------------------
// Skip-and-warn + error codes
// ---------------------------------------------------------------------------

/**
 * The 6 per-appendix skip-and-warn codes. On the binary `/download` these ride
 * in the `X-Compliance-Warnings` header (base64 JSON); for JSON CDG responses
 * they would populate an envelope `warnings[]` sibling.
 */
export type ComplianceWarningType =
  | 'merge_failed_missing_blob'
  | 'merge_failed_forbidden'
  | 'merge_failed_corrupt_pdf'
  | 'merge_failed_password_protected_pdf'
  | 'merge_failed_storage_unavailable'
  | 'merge_failed_storage_error';

/** Envelope error/warning detail — `error.details[]` and `warnings[]` share this shape. */
export type ComplianceDetail = {
  type: string;
  field: string;
  value: string;
};

/** A skip-and-warn warning — same shape as `ComplianceDetail` with a typed `type`. */
export type ComplianceWarningDetail = {
  type: ComplianceWarningType;
  field: string;
  value: string;
};

/** Error codes surfaced by CDG endpoints. */
export type ComplianceErrorCode =
  | 'merge_failed' // 409 unrecoverable
  | 'merge_too_many_appendices' // 400 cap: >50 files (DL-60055)
  | 'merge_payload_too_large' // 400 cap: >200 MB
  | 'action_not_allowed' // 403
  | 'entity_not_found'; // 404

/** Success code returned by DELETE (200 + archived entity, NOT 204). */
export const COMPLIANCE_DOCUMENT_DELETED_CODE = 'compliance_document_deleted';

// ---------------------------------------------------------------------------
// Binary /download channel — warnings ride in headers
// ---------------------------------------------------------------------------

/** Response header carrying base64 JSON `ComplianceWarningDetail[]` on POST /download. */
export const X_COMPLIANCE_WARNINGS_HEADER = 'X-Compliance-Warnings';

/** Response header carrying the persisted document GUID on POST /download. */
export const X_COMPLIANCE_DOCUMENT_ID_HEADER = 'X-ComplianceDocumentId';

/**
 * Parsed result of a `/download` call. Assembled by the download hook
 * (DL-59572): the binary body becomes a `Blob`, and `warnings` are decoded from
 * the `X-Compliance-Warnings` header (base64 JSON). NOTE: because the download
 * endpoint requests `responseType: 'arraybuffer'`, a non-2xx error envelope
 * arrives as an ArrayBuffer and must be text-decoded before JSON.parse.
 */
export type ComplianceDownloadResult = {
  documentId: string | null;
  blob: Blob;
  warnings: ComplianceWarningDetail[];
};

// ---------------------------------------------------------------------------
// Query keys (consumed by the hooks) — full key = [base, String(orgId), ...ids]
// ---------------------------------------------------------------------------

/**
 * Base query-key segments for CDG hooks. The hooks compose the full key as
 * `[base, String(orgId), ...ids]` (e.g. `['compliance-document-list', orgId, query]`).
 * Mutations invalidate by the relevant base key — never a magic string.
 */
export const complianceQueryKeys = {
  documentList: 'compliance-document-list',
  documentById: 'compliance-document',
  availableCerts: 'compliance-available-certs',
  templates: 'compliance-doc-templates',
  templateFields: 'compliance-doc-template-fields',
  generatorStyles: 'compliance-generator-styles',
  preview: 'compliance-generator-preview',
} as const;

export type ComplianceQueryKey = (typeof complianceQueryKeys)[keyof typeof complianceQueryKeys];

/**
 * Allowed server-side filter keys for the document list. The list hook should
 * drop any `TableState.filter` key not in this allowlist, and the MirageJS
 * handler honours exactly these.
 */
export const complianceDocumentFilterKeys = [
  'q',
  'status',
  'templateType',
  'styleId',
  'createdBy',
  'sort',
] as const;

export type ComplianceDocumentFilterKey = (typeof complianceDocumentFilterKeys)[number];
